import React, { useEffect, useState } from 'react';
import { X, Plus, Trash2 } from 'lucide-react';
import api from '../utils/api';
import './CitizenshipManagementPopover.css';

function CitizenshipManagementPopover({ onClose }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deletingKey, setDeletingKey] = useState(null);
  const [form, setForm] = useState({ dreamapply: '', neptun: '', neptun_country: '', eea_member: false });

  const loadItems = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await api.get('/api/admin/citizenships');
      setItems(response.data || []);
    } catch (err) {
      console.error('Failed to load citizenship mappings:', err);
      setError(err.response?.data?.detail || 'Állampolgárságok betöltése sikertelen');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadItems();
  }, []);

  const handleChange = (e) => {
    const { name, type, checked, value } = e.target;
    const fieldValue = type === 'checkbox' ? checked : value;
    setForm((prev) => ({ ...prev, [name]: fieldValue }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      setError('');
      const payload = {
        dreamapply: form.dreamapply.trim(),
        neptun: form.neptun.trim(),
        neptun_country: form.neptun_country.trim() || null,
        eea_member: !!form.eea_member,
      };
      if (!payload.dreamapply || !payload.neptun) {
        setError('Mindkét mező kitöltése kötelező.');
        setSaving(false);
        return;
      }
      const response = await api.post('/api/admin/citizenships', payload);
      const saved = response.data;
      setItems((prev) => {
        const others = prev.filter((i) => i.dreamapply !== saved.dreamapply);
        return [...others, saved].sort((a, b) => a.dreamapply.localeCompare(b.dreamapply));
      });
      setForm({ dreamapply: '', neptun: '', neptun_country: '', eea_member: false });
      setShowAddForm(false);
    } catch (err) {
      console.error('Failed to save citizenship mapping:', err);
      setError(err.response?.data?.detail || 'Mentés sikertelen');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (item) => {
    setForm({
      dreamapply: item.dreamapply,
      neptun: item.neptun,
      neptun_country: item.neptun_country || '',
      eea_member: !!item.eea_member,
    });
    setShowAddForm(true);
  };

  const handleDelete = async (dreamapply) => {
    if (!window.confirm('Biztosan törölni szeretné ezt az állampolgárság-mappolást?')) return;
    try {
      setDeletingKey(dreamapply);
      const encoded = encodeURIComponent(dreamapply);
      await api.delete(`/api/admin/citizenships/${encoded}`);
      setItems((prev) => prev.filter((i) => i.dreamapply !== dreamapply));
    } catch (err) {
      console.error('Failed to delete citizenship mapping:', err);
      alert(err.response?.data?.detail || 'Törlés sikertelen');
    } finally {
      setDeletingKey(null);
    }
  };

  return (
    <div className="cit-mgmt-overlay">
      <div className="cit-mgmt-popover">
        <div className="cit-mgmt-header">
          <h2>Állampolgárság-kezelés</h2>
          <button className="icon-button" onClick={onClose}>
            <X size={18} />
          </button>
        </div>

        {error && <div className="cit-mgmt-error">{error}</div>}

        <div className="cit-mgmt-toolbar">
          <button
            className="add-item-button"
            type="button"
            onClick={() => setShowAddForm(true)}
          >
            <Plus size={16} />
            Új ország
          </button>
        </div>

        {showAddForm && (
          <div className="cit-mgmt-add-form-wrapper">
            <form className="cit-mgmt-add-form" onSubmit={handleSave}>
              <div className="form-row">
                <label>DreamApply érték (pl. "US United States")</label>
                <input
                  type="text"
                  name="dreamapply"
                  required
                  value={form.dreamapply}
                  onChange={handleChange}
                  disabled={saving}
                />
              </div>
              <div className="form-row form-row-inline">
                <label>
                  <input
                    type="checkbox"
                    name="eea_member"
                    checked={!!form.eea_member}
                    onChange={handleChange}
                    disabled={saving}
                  />
                  {' '}EEA tagállam
                </label>
              </div>
              <div className="form-row">
                <label>Neptun állampolgárság (magyar melléknév)</label>
                <input
                  type="text"
                  name="neptun"
                  required
                  value={form.neptun}
                  onChange={handleChange}
                  disabled={saving}
                />
              </div>
              <div className="form-row">
                <label>Neptun ország (magyar főnév)</label>
                <input
                  type="text"
                  name="neptun_country"
                  value={form.neptun_country}
                  onChange={handleChange}
                  disabled={saving}
                />
              </div>
              <div className="form-actions">
                <button
                  type="button"
                  className="secondary-button"
                  onClick={() => setShowAddForm(false)}
                  disabled={saving}
                >
                  Mégse
                </button>
                <button type="submit" className="primary-button" disabled={saving}>
                  {saving ? 'Mentés...' : 'Mentés'}
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="cit-mgmt-list">
          {loading ? (
            <div className="cit-mgmt-loading">Betöltés...</div>
          ) : items.length === 0 ? (
            <div className="cit-mgmt-empty">Még nincsenek állampolgárság-mappolások.</div>
          ) : (
            items
              .slice()
              .sort((a, b) => a.dreamapply.localeCompare(b.dreamapply))
              .map((item) => (
                <div key={item.dreamapply} className="cit-mgmt-item">
                  <div className="cit-mgmt-info" onDoubleClick={() => handleEdit(item)}>
                    <div className="cit-mgmt-dreamapply">{item.dreamapply}</div>
                    <div className="cit-mgmt-neptun">
                      {item.neptun}
                      {item.neptun_country ? ` - ${item.neptun_country}` : ''}
                      {item.eea_member && <span className="cit-mgmt-eea-badge">EEA</span>}
                    </div>
                  </div>
                  <div className="cit-mgmt-actions">
                    <button
                      type="button"
                      className="icon-button"
                      onClick={() => handleEdit(item)}
                    >
                      Szerkesztés
                    </button>
                    <button
                      type="button"
                      className="icon-button danger"
                      onClick={() => handleDelete(item.dreamapply)}
                      disabled={deletingKey === item.dreamapply}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              ))
          )}
        </div>
      </div>
    </div>
  );
}

export default CitizenshipManagementPopover;
